import LinkIcon from '@atlaskit/icon/glyph/link';
import UnlinkIcon from '@atlaskit/icon/glyph/unlink';
import { Flex, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { useFlaggingCallback } from '@databases/flags';
import ValidationStatus, { Placement } from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { DatabaseView } from '@databases/types/database/view';
import { isUnsavedView } from '@databases/utils/database/view/view';
import { isEmbed } from '@databases/utils/utils';
import { HoverButton } from '@databases/view-tabs/HoverButton';
import ViewActions from '@databases/view-tabs/ViewActions';
import { messages as viewsMessages } from '@databases/views/messages';
import { ViewPanelSettingType } from '@databases/views/types';
import useCopyViewLink from '@databases/views/useCopyViewLink';
import { getLayoutIcons } from '@databases/views/utils';
import React, { FunctionComponent, ReactChild, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import ViewCardNameEdit, { ViewNameContainer } from './ViewCardNameEdit';

const PADDING_VALUES = {
  DEFAULT_ACTIVE_PADDING: '70px',
  DEFAULT_PADDING: '6px',
  UNSAVED_VIEW_ACTIVE_PADDING: '40px',
};

const DefaultText = styled.span`
  color: ${colors.subtleText};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  font-style: italic;
  text-align: right;
  padding-right: ${PADDING_VALUES.DEFAULT_PADDING};
  position: absolute;
  right: 6px;
`;

const Container = styled.div<{
  isActive?: boolean;
  isDefault: boolean;
  isViewActionsOpen: boolean;
  isUnsavedView?: boolean;
  showViewSettings?: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 4px;

  z-index: 0;
  padding: 8px 0px;
  margin: 0px 0px 0px 4px;

  border-radius: 3px;
  cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
  color: ${(props) =>
    props.isActive ? token('color.text.selected', colors.B300) : colors.N400};
  }

  & ${DefaultText} {
    padding-right: ${(props) => {
      if (props.isViewActionsOpen) {
        return PADDING_VALUES.DEFAULT_ACTIVE_PADDING;
      }
      return PADDING_VALUES.DEFAULT_PADDING;
    }}
  }

  &:hover,
  &:focus-within {
    ${HoverButton} {
      width: 32px;
      visibility: visible;
      display: inline-flex;
      opacity: 1;
    }

    ${DefaultText} {
      ${(props) => {
        if (props.isUnsavedView) {
          return `
            padding-right: ${PADDING_VALUES.UNSAVED_VIEW_ACTIVE_PADDING};
          `;
        }

        return `
          padding-right: ${PADDING_VALUES.DEFAULT_ACTIVE_PADDING};
        `;
      }}
    }

    ${ViewNameContainer} {
      max-width: ${(props) => (props.isDefault ? '180px' : '240px')} !important;
    }
  }
`;

const SubtleText = styled.div`
  color: ${colors.subtleText};
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
`;

function getViewProps(
  view: DatabaseView,
  isUnsavedView?: boolean,
  isDefault = false
): { name: string; icon: ReactChild } {
  const type = view.settings.layout.type;
  return {
    icon: isUnsavedView ? (
      <UnlinkIcon label="" size="medium" />
    ) : (
      getLayoutIcons(type, 'medium', isDefault, false)?.icon
    ),
    name: view.name,
  };
}

export interface ViewCardProps {
  view: DatabaseView;
  isActive?: boolean;
  isDefault: boolean;
  onClick: (view: DatabaseView) => void;
  subtleText?: string;
  isReadonly?: boolean;
  contentId: string;
  existingViewNames?: string[];
  setDuplicateViewNameErrorId: (value: string) => void;
  hasDuplicateNameError: boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  handleClose: () => void;
  showViewSettings?: boolean;
}

const ViewCard: FunctionComponent<ViewCardProps> = ({
  view,
  isActive,
  isDefault,
  onClick,
  subtleText,
  isReadonly,
  contentId,
  existingViewNames,
  setDuplicateViewNameErrorId,
  hasDuplicateNameError,
  openViewSettingsPanel,
  handleClose,
  showViewSettings = true,
}) => {
  const [isViewActionsOpen, setIsViewActionsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const client = useAnalytics();
  const { formatMessage } = useIntl();

  const handleClick = useFlaggingCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      await onClick(view);
    },
    [view, onClick]
  );

  const onCopyViewLink = useCopyViewLink(contentId);

  const handleCopyViewLink = useFlaggingCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const result = onCopyViewLink(view);

      if (result) {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 3000);
        await client.sendViewLinkCopiedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          source: 'databaseViewPicker',
          viewId: view.id,
        });
      } else {
        throw new Error(formatMessage(messages['toolbar-views.copy-link-error']));
      }
    },
    [client, contentId, formatMessage, onCopyViewLink, view]
  );

  const { icon, name } = getViewProps(view, isUnsavedView(view), isDefault);

  const getTranslatedName = useMemo(() => {
    if (isUnsavedView(view)) {
      return formatMessage(messages['view.unsaved-view']);
    }

    return name;
  }, [formatMessage, name, view]);

  const tooltipContent = useMemo(() => {
    if (isUnsavedView(view)) {
      return formatMessage(messages['toolbar-views.embed-visible-tooltip']);
    } else if (isDefault) {
      return formatMessage(messages['toolbar-views.default-view-tooltip'], {
        isEmbed: isEmbed(),
      });
    } else {
      return '';
    }
  }, [formatMessage, view, isDefault]);

  return (
    <ValidationStatus
      isTouched={hasDuplicateNameError}
      placement={Placement.BOTTOM}
      error={
        hasDuplicateNameError
          ? formatMessage(messages['toolbar-views.duplicate-view-warning-text'])
          : undefined
      }
    >
      {(validationProps) => (
        <Tooltip content={tooltipContent} position="top">
          <Container
            isActive={isActive}
            onClick={handleClick}
            isDefault={isDefault}
            isViewActionsOpen={isViewActionsOpen}
            isUnsavedView={isUnsavedView(view)}
            showViewSettings={showViewSettings}
          >
            {icon}
            <Flex alignItems="start" direction="column" xcss={xcss({ flexGrow: 1 })}>
              <Flex
                alignItems="center"
                columnGap="space.050"
                justifyContent="space-between"
                xcss={xcss({ flexGrow: 1, width: '100%', minHeight: token('space.300') })}
              >
                <ViewCardNameEdit
                  name={getTranslatedName}
                  existingViewNames={existingViewNames}
                  view={view}
                  setDuplicateViewNameErrorId={setDuplicateViewNameErrorId}
                  validationProps={validationProps}
                  isActive={isActive}
                  isDefault={isDefault}
                  isViewActionsOpen={isViewActionsOpen}
                />
                <Flex alignItems="center">
                  <>
                    {isDefault && (
                      <DefaultText
                        className="view-card-default-text"
                        isViewActionsOpen={isViewActionsOpen}
                      >
                        {formatMessage(messages['toolbar-views.default-text'])}
                      </DefaultText>
                    )}
                    {showViewSettings && (
                      <Tooltip
                        content={formatMessage(
                          isCopied
                            ? messages['copy-link.tooltip']
                            : viewsMessages['view-settings.copy-link']
                        )}
                      >
                        <HoverButton
                          iconBefore={
                            <LinkIcon
                              label={formatMessage(
                                viewsMessages['view-settings.copy-link']
                              )}
                            />
                          }
                          onClick={handleCopyViewLink}
                          appearance="subtle"
                          spacing="compact"
                          isActive={isActive}
                          isViewActionsOpen={isViewActionsOpen}
                          className="view-card-button"
                        />
                      </Tooltip>
                    )}
                    {!isUnsavedView(view) && showViewSettings && (
                      <ViewActions
                        contentId={contentId}
                        view={view}
                        isViewActive={isActive}
                        isOpen={isViewActionsOpen}
                        setIsViewActionsOpen={setIsViewActionsOpen}
                        isDefault={isDefault}
                        isReadonly={isReadonly}
                        openViewSettingsPanel={openViewSettingsPanel}
                        handleCloseViewPicker={handleClose}
                      />
                    )}
                  </>
                </Flex>
              </Flex>

              <SubtleText>{subtleText}</SubtleText>
            </Flex>
          </Container>
        </Tooltip>
      )}
    </ValidationStatus>
  );
};

export default ViewCard;
